import {
  ClipboardEvent,
  KeyboardEvent,
  FocusEvent,
  FormEvent,
  useRef,
  useState,
} from "react";
import { useMutation } from "@apollo/client";
import { stripHtml } from "string-strip-html";

import {
  CLIP_COMMENT_CREATE,
  CLIP_COMMENT_FIELDS,
} from "MVVM/GraphQL/clipComments";
import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";

const MAX_COMMENT_SIZE = 600;

export const useClipCommentForm = ({
  clipId,
  setPortalOpen,
}: {
  clipId: string;
  setPortalOpen?: (value: boolean) => void;
}) => {
  const placeholder = "Add a comment...";
  const [comment, setComment] = useState(placeholder);
  const [disabled, setDisabled] = useState(true);
  const inputRef = useRef<HTMLElement | null>(null);
  const [createComment, { loading }] = useMutation(CLIP_COMMENT_CREATE);
  const { allstarUser } = useUser();

  const handleFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!allstarUser.loggedIn && setPortalOpen) {
      e.target.blur();

      return setPortalOpen(true);
    }

    setComment("");
    setDisabled(false);
  };

  const submit = (comment: string) => {
    createComment({
      variables: {
        clipId,
        text: comment,
      },
      onCompleted: () => {
        setComment("");
        if (inputRef.current) inputRef.current.textContent = "";
        setDisabled(true);

        analytics.track("Comment Created", {
          platform: "web",
        });
      },
      update(cache, { data: { commentCreate } }) {
        const newCommentRef = cache.writeFragment({
          data: commentCreate.comment,
          fragment: CLIP_COMMENT_FIELDS,
        });

        const cacheId =
          cache.identify({
            __typename: "Clip",
            id: commentCreate.comment.clip.id,
          }) ||
          cache.identify({
            __typename: "Clip",
            id: clipId,
          });

        if (!cacheId) {
          return;
        }

        cache.modify({
          id: cacheId,
          fields: {
            comments(existingComments = {}) {
              const existingNodes = existingComments.nodes || [];
              const updatedComments = {
                ...existingComments,
                nodes: [newCommentRef, ...existingNodes],
                pageInfo: {
                  ...existingComments.pageInfo,
                  totalCount: (existingComments.pageInfo?.totalCount || 0) + 1,
                },
              };
              return updatedComments;
            },
          },
        });
      },
    });
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const text = e.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (!selection) return;

    const range = selection.getRangeAt(0);
    const textNode = document.createTextNode(text);
    range.deleteContents();
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);

    const el = e.currentTarget;
    const newContent = el.textContent || "";
    setComment(newContent);
    setDisabled(!newContent.trim() || newContent === placeholder);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      validateComment();
    }
  };

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    const textContent = e.currentTarget.textContent || "";
    setComment(textContent);

    const hasContent = textContent.trim() && textContent !== placeholder;
    setDisabled(!hasContent);
  };

  const handleClick = () => validateComment();

  const validateComment = () => {
    const actualContent = inputRef.current?.textContent || "";
    const trimmedComment = actualContent.trim();

    if (
      !allstarUser.loggedIn ||
      !trimmedComment ||
      trimmedComment === placeholder ||
      loading
    ) {
      return;
    }

    const strippedComment = stripHtml(trimmedComment).result;

    submit(
      strippedComment.length > MAX_COMMENT_SIZE
        ? `${strippedComment.substring(0, MAX_COMMENT_SIZE)}...`
        : strippedComment,
    );
  };

  const handleEmojiSelect = (emoji: string) => {
    if (!inputRef.current) return;

    const currentContent = inputRef.current.textContent || "";
    const isPlaceholder =
      currentContent === placeholder || currentContent === "";
    const newContent = isPlaceholder ? emoji : currentContent + emoji;

    inputRef.current.textContent = newContent;
    setComment(newContent);
    setDisabled(false);
    inputRef.current.focus();

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(inputRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return {
    loading,
    inputRef,
    disabled,
    handleFocus,
    handleKeyDown,
    handleInput,
    handlePaste,
    handleClick,
    handleEmojiSelect,
  };
};
